import React, { useState } from 'react';
import { DomainCardButton } from '../../domain-cards/ui';

/**
 * DayNightCyclePattern - Pattern for week-long day/night environmental phases
 *
 * Config properties used:
 * - cycleDuration: 'week' or other duration
 * - phases: array of phase definitions
 * - gmControlled: whether only GM can change phase
 */
const DayNightCyclePattern = ({
  config,
  sessionState,
  setSessionState,
  onClose,
}) => {
  const phases = config.phases || [
    { id: 'day', name: 'Day Week', icon: '☀️', description: 'The sun shines for a full week.' },
    { id: 'night', name: 'Night Week', icon: '🌙', description: 'Darkness covers the land for a full week.' },
  ];

  const currentPhase = sessionState?.currentPhase || phases[0]?.id || 'day';
  const currentPhaseData = phases.find(p => p.id === currentPhase) || phases[0];

  const handleTogglePhase = () => {
    const currentIndex = phases.findIndex(p => p.id === currentPhase);
    const nextIndex = (currentIndex + 1) % phases.length;
    setSessionState?.({ ...sessionState, currentPhase: phases[nextIndex].id });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-indigo-600 dark:text-indigo-400 mb-1">
          🌓 Day/Night Cycle
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The Witherwild's week-long day and night cycles
        </p>
      </div>

      {/* Current Phase Display */}
      <div className={`p-6 rounded-lg border-2 text-center ${
        currentPhase === 'day'
          ? 'bg-yellow-900/20 border-yellow-500'
          : 'bg-indigo-900/20 border-indigo-500'
      }`}>
        <p className="text-5xl mb-2">{currentPhaseData.icon}</p>
        <p className="text-xl font-bold">{currentPhaseData.name}</p>
        <p className="text-sm text-gray-400 mt-2">
          {currentPhaseData.description}
        </p>
      </div>

      {/* Environment Modifiers */}
      {currentPhaseData.environmentModifiers && (
        <div className="bg-gray-800 p-4 rounded-lg">
          <h4 className="font-medium mb-2 text-gray-300">Environment</h4>
          <ul className="text-sm text-gray-400 space-y-1">
            {currentPhaseData.environmentModifiers.flora && (
              <li>🌿 Flora: {Array.isArray(currentPhaseData.environmentModifiers.flora)
                ? currentPhaseData.environmentModifiers.flora.join(', ')
                : currentPhaseData.environmentModifiers.flora}</li>
            )}
            {currentPhaseData.environmentModifiers.fauna && (
              <li>🦎 Fauna: {currentPhaseData.environmentModifiers.fauna}</li>
            )}
            {currentPhaseData.environmentModifiers.visibility && (
              <li>👁️ Visibility: {currentPhaseData.environmentModifiers.visibility}</li>
            )}
          </ul>
        </div>
      )}

      {/* Toggle Button (GM only in actual implementation) */}
      <DomainCardButton
        onClick={handleTogglePhase}
        variant="primary"
        className="w-full"
      >
        Advance to Next Phase
      </DomainCardButton>

      <DomainCardButton onClick={onClose} variant="secondary" className="w-full">
        Close
      </DomainCardButton>
    </div>
  );
};

export default DayNightCyclePattern;
